<template>
  <Modal
    v-model:visible="isVisible"
    class="bulk-tender-modal"
    :header="t('shipments.bulkTender')"
    size="md"
    maximizable
    :no-scroll="isAnyLoading"
    @keyup.esc="onCancel"
  >
    <div v-if="isAnyLoading" class="bulk-tender-loading-container">
      <h3 v-if="loadingBulkTender">
        {{
          t('shipments.tenderShipmentsCounts', {
            count: numberOfShipments,
          })
        }}
      </h3>
      <LoadingIndicator loading />
    </div>

    <ul v-else class="bulk-tender-shipments-list">
      <li v-for="group in bulkTenderGroups" :key="group.scac">
        <BulkTenderCard
          v-model:tender-method="group.tenderMethod"
          v-model:tender-config-options="group.tenderConfigOptions"
          :number-of-shipments="group.numberOfShipments"
          :carrier-name="group.carrierName"
          :scac="group.scac"
          :carrier-settings="group.carrierSettings"
          :available-tender-methods="
            getAvailableTenderMethods(group.carrierSettings)
          "
          class="flex-grow-1"
        />
      </li>
    </ul>

    <template #footer>
      <RygenButton
        :disabled="isAnyLoading"
        mode="tertiary"
        :label="t('common.cancel')"
        @click="onCancel"
      />
      <RygenButton
        :loading="loadingBulkTender"
        :disabled="loadingCarrierSettings"
        mode="primary"
        icon="pi pi-send"
        :label="t('shipments.tender')"
        @click="onSend"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import axios from 'axios';
import { computed, PropType, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { useToast, useValidations } from '@rygen/rygen-ui/composables';
import {
  CarrierSetting,
  TenderConfig,
  TenderConfigOptions,
  TenderMethod,
} from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';
import { Shipment as EsShipment } from '@rygen/tms-search-model/search/shipment';

import api from '@/api';
import {
  BulkTenderRequest,
  BulkTenderResult,
  ShipmentSearchData,
} from '@/model';

import BulkTenderCard from '@/components/shipment/BulkTenderCard.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import LoadingIndicator from '@rygen/rygen-ui/components/ui/LoadingIndicator.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';

type BulkTenderGroup = {
  carrierName: string;
  scac: string;
  numberOfShipments: number;
  carrierSettings: CarrierSetting;
  tenderMethod: TenderMethod;
  tenderConfigOptions: TenderConfigOptions;
};

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  selectedShipments: {
    type: Array as PropType<ShipmentSearchData[] | EsShipment[]>,
    default: () => [],
  },
});
const { selectedShipments } = toRefs(props);
const emit = defineEmits<{
  (event: 'cancel'): void;
  (event: 'shipments-tendered', results: BulkTenderResult[]): void;
}>();

const { t } = useI18n();

const toast = useToast();

const loadingCarrierSettings = ref(false);
const loadingBulkTender = ref(false);
const bulkTenderGroups = ref<BulkTenderGroup[]>([]);

const { validate } = useValidations(bulkTenderGroups, {});

const getAvailableTenderMethods = (
  carrierSettings: CarrierSetting,
): TenderMethod[] => {
  return (
    carrierSettings.tenderConfig
      .map((config) => config.method)
      .filter(notNull) ?? []
  );
};

const getDefaultTenderMethod = (
  carrierSettings: CarrierSetting,
): TenderMethod => {
  const defaultConfig = carrierSettings.tenderConfig.find(
    (config) => config.defaultMethod,
  );
  return defaultConfig?.method;
};

const getDefaultTenderConfigOptions = (
  carrierSettings: CarrierSetting,
): TenderConfigOptions => {
  const config = carrierSettings.tenderConfig.find(
    (config) => config.defaultMethod,
  );
  return new TenderConfigOptions(config?.configOptions);
};

const isAnyLoading = computed(
  () => loadingCarrierSettings.value || loadingBulkTender.value,
);

const numberOfShipments = computed(() => {
  return selectedShipments.value.length;
});

const groupSelectedShipmentsByScac = async () => {
  const scacGroups = selectedShipments.value.reduce((map, shipment) => {
    const scac = ShipmentSearchData.isOldSearchModel(shipment)
      ? shipment.scac
      : shipment.carrier?.scac;
    if (!scac) {
      return map;
    }

    const shipmentEntry = map.get(scac);
    const carrierName = ShipmentSearchData.isOldSearchModel(shipment)
      ? shipment.carrierName
      : shipment.carrier?.name;
    map.set(scac, {
      carrierName: carrierName ?? '',
      count: (shipmentEntry?.count ?? 0) + 1,
    });

    return map;
  }, new Map<string, { carrierName: string; count: number }>());

  const groups: BulkTenderGroup[] = [];
  for (const [scac, { carrierName, count }] of scacGroups.entries()) {
    const carrierSettings =
      (await loadCarrierSettings(scac)) ||
      createManualCarrierSetting(scac, carrierName);

    const tenderMethod = getDefaultTenderMethod(carrierSettings);
    const tenderConfigOptions = getDefaultTenderConfigOptions(carrierSettings);
    groups.push({
      scac,
      numberOfShipments: count,
      carrierName,
      carrierSettings,
      tenderMethod,
      tenderConfigOptions,
    });
  }

  bulkTenderGroups.value = groups;
};

const createManualCarrierSetting = (scac: string, carrierName: string) => {
  const defaultTenderConfig = new TenderConfig({
    method: TenderMethod.MANUAL,
    defaultMethod: true,
  });

  return new CarrierSetting({
    carrier: {
      name: carrierName,
      scac: scac,
    },
    tenderConfig: [defaultTenderConfig],
  });
};

const onCancel = () => {
  emit('cancel');
  isVisible.value = false;
};

const onSend = async () => {
  if (!(await validate())) {
    return;
  }

  loadingBulkTender.value = true;
  try {
    const bulkTenderRequests = bulkTenderGroups.value.map(
      ({ scac, tenderMethod, tenderConfigOptions }) => {
        const tenderConfig = new TenderConfig({
          method: tenderMethod,
          configOptions: tenderConfigOptions,
        });

        return new BulkTenderRequest({
          shipmentIds: selectedShipments.value
            .filter((shipment) => {
              const shipmentScac = ShipmentSearchData.isOldSearchModel(shipment)
                ? shipment.scac
                : shipment.carrier?.scac;

              return shipmentScac === scac;
            })
            .map((shipment) => shipment.id)
            .filter(notNull),
          scac,
          tenderConfig,
        });
      },
    );

    const results = await api.shipmentBatch.bulkTender({
      bulkTenderRequests,
    });
    isVisible.value = false;
    emit('shipments-tendered', results);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error bulk tendering shipments', e);
    toast.addError({
      error: e,
      detail: t('shipments.errors.bulkTender'),
    });
  } finally {
    loadingBulkTender.value = false;
  }
};

const loadCarrierSettings = async (scac: string) => {
  if (!scac || !authorization.hasAuthority('client', 'read')) {
    return undefined;
  }
  try {
    loadingCarrierSettings.value = true;
    let settings = await api.carrier.findCarrierSettingsForScac(scac);

    if (!settings) {
      settings = await api.broker.findBrokerSettingsForScac(scac);
    }

    return settings;
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.warn(
      t('errors.loadingParam', {
        param: 'carrier settings: Carrier setting my not be configured yet',
      }),
      e,
    );
  } finally {
    loadingCarrierSettings.value = false;
  }
};

watch(isVisible, async (newValue) => {
  if (newValue) {
    await groupSelectedShipmentsByScac();
  }
});
</script>

<style>
.bulk-tender-modal.rygen-modal.p-dialog {
  &.performing-setup {
    .p-dialog-content {
      .modal-content {
        min-height: 55vh;

        div {
          display: flex;
          gap: var(--sp-2);
          justify-content: center;
          align-items: center;
        }
      }
    }
  }

  .bulk-tender-loading-container {
    display: flex;
    align-items: center;
    gap: var(--sp-2);
    justify-content: center;
  }

  .p-message {
    margin-bottom: var(--sp-3);
  }

  .bulk-tender-shipments-list {
    display: flex;
    flex-direction: column;
    gap: var(--gap-1);
    margin: var(--sp-3) 0;
    padding: 0;
  }

  .p-dialog-footer .modal-footer {
    justify-content: end;
  }
}
</style>
